import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Images } from "lucide-react";

import PageWrapper from "../../../../components/layout/page/PageWrapper";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";

import Text from "../../../../components/typography/Text";

import BackButton from "../../../../components/buttons/back/BackButton";
import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";

import AlbumPhotoGrid from "../../components/AlbumPhotoGrid";
import AlbumGalleryModal from "../../components/AlbumGalleryModal";

import { useAlbumGallery } from "../hooks/useAlbumGallery";

import "./SharedAlbumPhotosPage.css";

export default function SharedAlbumPhotosPage() {
  const navigate = useNavigate();
  const { albumTag } = useParams();

  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const { album, images, loading, error, refresh } = useAlbumGallery(albumTag);

  const photoList = Array.isArray(images) ? images : [];

  const title = album?.title || "Album";
  const description = album?.description || "";

  const channelName = album?.brandName || album?.username || "Katch";

  const channelUrl = album?.username
    ? `/${encodeURIComponent(album.username)}`
    : null;

  const initials = String(channelName).trim().charAt(0).toUpperCase();

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error.message || "Unable to load album photos."
    : "";

  const handleOpenGallery = (index) => {
    if (!photoList[index]) return;

    setSelectedPhoto(index);
  };

  const handleCloseGallery = () => {
    setSelectedPhoto(null);
  };

  const renderChannel = () => {
    if (!channelUrl) return null;

    return (
      <Link to={channelUrl} className="album-gallery-page__channel">
        {album?.avatarUrl ? (
          <img
            src={album.avatarUrl}
            alt=""
            className="album-gallery-page__channel-avatar"
          />
        ) : (
          <span className="album-gallery-page__channel-placeholder">
            {initials}
          </span>
        )}

        <span className="album-gallery-page__channel-info">
          <strong>{channelName}</strong>
          <small>@{album.username}</small>
        </span>
      </Link>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="album-gallery-page__loading" role="status">
          <Text>Loading photos...</Text>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <EmptyState
          icon={<Images size={28} />}
          title="Unable to load photos"
          description={errorMessage}
          action={
            <Button
              value="Try Again"
              variant="primary-outlined"
              onClick={refresh}
            />
          }
        />
      );
    }

    if (!photoList.length) {
      return (
        <EmptyState
          icon={<Images size={28} />}
          title="No photos available"
          description="There are no photos in this album yet."
        />
      );
    }

    return (
      <AlbumPhotoGrid images={photoList} onPhotoClick={handleOpenGallery} />
    );
  };

  return (
    <PageWrapper>
      <BackButton onClick={() => navigate(-1)} />
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <ContentPanel>
          <ContentPanelHeader title={title} marginTop={0} marginBottom={24} />
          {renderChannel()}
        </ContentPanel>

        <ContentPanel>
          <ContentPanelHeader
            title="Album Photos"
            subTitle={description || `${photoList.length} photos`}
          ></ContentPanelHeader>

          {renderContent()}
        </ContentPanel>
      </div>

      {selectedPhoto !== null && !loading && !errorMessage && (
        <AlbumGalleryModal
          open
          images={photoList}
          initialIndex={selectedPhoto}
          title={title}
          description={description}
          brandName={album?.brandName}
          username={album?.username}
          avatarUrl={album?.avatarUrl}
          onClose={handleCloseGallery}
        />
      )}
    </PageWrapper>
  );
}
